import {
  ICellRendererParams,
  ValueFormatterParams,
  ColDef,
  GridReadyEvent,
  GridApi
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Stack from "react-bootstrap/Stack";
import { LinkContainer } from "react-router-bootstrap";
import { useParams } from "react-router-dom";
import { useImmer } from "use-immer";
import {
  ApiException,
  DeliveryDateDTO,
  ItemType,
  OrderClient,
  OrderDTO,
  UnprocessedViewDTO,
} from "../../../services/ApiClient";
import { CardPlaceholder } from "../../common/cardPlaceholder/CardPlaceholder";
import { DeliveryDatePicker } from "../../common/deliveryDatePicker/DeliveryDatePicker";
import { NoCards } from "../../layouts/NoCards";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import "./UnprocessedOrders.scss";
import { UpdateDateConfirmModal } from "./UpdateDateConfirmModal";

interface INewOrderProps {}

export const UnprocessedOrders = (props: INewOrderProps) => {
  const params = useParams();
  const [error, setError] = useState<null | string>(null);
  const [isLoaded, setIsLoaded] = useState(false);     // Page is loaded
  const [isFetching, setIsFetching] = useState(false); // React is getting data from database
  const [possibleDeilveryDates, setPossibleDilveryDates] = useState<DeliveryDateDTO[]>([]); // Options for the Delivery date dropdown
  const [currentDeliveryDate, setCurrentDeliveryDate] = useState<DeliveryDateDTO | null>(null); // Selected Delivery date
  const [unprocessedViewDto, setUnprocessedViewDto] = useImmer<UnprocessedViewDTO | null>(null); // Unconfirmed orders
  const [hasUpdated, setHasUpdated] = useState<boolean>(true); // True if anything on the page has been updated

  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [itemUpateDate, setItemUpateDate] = useState<OrderDTO | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [selectedCount, setSelectedCount] = useState(0);

  // Load the orders
  useEffect(() => {
    const fetchNewOrder = async () => {
      try {
        if (hasUpdated) {
          setIsFetching(true);

          const orderType = params.orderType;
          let orderTypeIndex;

          //Pots = 0,
          //Cuts = 1,
          //Materials = 2,

          if (orderType === "pots") {
            orderTypeIndex = 0;
          } else if (orderType === "cut") {
            orderTypeIndex = 1;
          } else if (orderType === "materials") {
            orderTypeIndex = 2;
          }

          //setOrderType(orderTypeIndex ?? null);

          if (currentDeliveryDate?.actualValue) {
            const filterDate = currentDeliveryDate?.actualValue;
            const unprocessedViewDtoResult = await new OrderClient(
              process.env.REACT_APP_API_BASE
            ).unconfirmedOrders(orderTypeIndex, filterDate);
            setUnprocessedViewDto(unprocessedViewDtoResult);
            setPossibleDilveryDates(unprocessedViewDtoResult.possibleDeliveryDates);
          } else {
            const unprocessedViewDtoResult = await new OrderClient(
              process.env.REACT_APP_API_BASE
            ).unconfirmedOrders(orderTypeIndex);
            setUnprocessedViewDto(unprocessedViewDtoResult);
            setPossibleDilveryDates(unprocessedViewDtoResult.possibleDeliveryDates);
          }

          setIsLoaded(true);
          setIsFetching(false);
          setHasUpdated(false);
        }
      } catch (e: unknown) {
        const error = e as ApiException;
        console.log(error.message);
        setIsFetching(false);
        setError(JSON.parse(error.response).error);
      }
    };

    fetchNewOrder().catch(console.error);
  }, [currentDeliveryDate, hasUpdated, params.orderType, setUnprocessedViewDto]);


  /********************************************************************************
  * Value formatters
  ********************************************************************************/
  const dateFormatter = (params: ValueFormatterParams) => {
    return dayjs(params.value).format("DD/MM/YY");
  };

  /********************************************************************************
  * Renderers
  ********************************************************************************/
  const ChangeDateCellRenderer = (props: ICellRendererParams): JSX.Element => {
    return (
      <div className={"btn-container"}>
        <Button
          size="sm"
          variant="primary"
          onClick={() => setItemUpateDate(props.data)}
        >
          {"Change delivery date"}
        </Button>
      </div>
    );
  };

  /********************************************************************************
  * Event handlers
  ********************************************************************************/
  const setDeliveryDate = useCallback(
    (delvieryDate: DeliveryDateDTO) => {
      setCurrentDeliveryDate(delvieryDate);
      setHasUpdated(true);
      setSelectedCount(0);
    },
    [setCurrentDeliveryDate]
  );

  // Handler for updatin an order delivery date
  const updateOrderDate = useCallback(
    async (orderId: number, orderDeliveryDate: string) => {
      try {
        await new OrderClient(
          process.env.REACT_APP_API_BASE
        ).updateDeliveryDate(orderId, orderDeliveryDate);
        const currentDeliveryDate = unprocessedViewDto!.possibleDeliveryDates.find(
          (deliveryDate) => {
            return deliveryDate.actualValue === orderDeliveryDate;
          },
          []
        );

        if (currentDeliveryDate) {
          setCurrentDeliveryDate(currentDeliveryDate);
        }

        setHasUpdated(true);
        setItemUpateDate(null);
      } catch (e: unknown) {
        const error = e as ApiException;
        console.log(error.message);
        setIsFetching(false);
        setError(JSON.parse(error.response).error);
      }
    },
    [setHasUpdated, setItemUpateDate, unprocessedViewDto]
  );

  // Original Delete order button (now obsolete)
  const deleteOrder = useCallback(
    async (orderId: number) => {
      try {
        await new OrderClient(process.env.REACT_APP_API_BASE).deleteOrder(orderId);

        setUnprocessedViewDto((draft) => {
          const updateIndex = draft?.orders.findIndex(
            (order) => order.orderId === orderId
          );
          if (updateIndex !== -1) draft?.orders.splice(updateIndex!, 1); // Remove 1 item from the position of updateIndex
        });

        setHasUpdated(true);
        
      } catch (e: unknown) {
        const error = e as ApiException;
        console.log(error.message);
        setIsFetching(false);
        setError(JSON.parse(error.response).error);
      }
    },
    [setUnprocessedViewDto]
  );

  // Ag Grid events
  const onGridReady = useCallback((params: GridReadyEvent) => {
      setGridApi(params.api);
  }, [setGridApi]);

  const onSelectionChanged = useCallback(() => {
    if (gridApi) {
      const selectedRows = gridApi.getSelectedRows();
      setSelectedCount(selectedRows.length);
    }
  }, [gridApi]);

  // Delete (all selected orders) button
  const deleteOrders = useCallback(
    async () => {
      try {
        const selectedOrders = gridApi?.getSelectedRows();
        const orderIds: number[] = selectedOrders?.map(order => order.orderId) || [];
        await new OrderClient(process.env.REACT_APP_API_BASE).deleteOrders(orderIds);

        setShowDeleteDialog(false);
        setSelectedCount(0);
        setHasUpdated(true);
      } catch (e: unknown) {
      const error = e as ApiException;
      console.log(error.message);
      setIsFetching(false);
      setError(JSON.parse(error.response).error);
      }
    },
    [gridApi, setHasUpdated]
  );
  

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Store",
        field: "store.name",
        flex: 4,
        checkboxSelection: true,
        headerCheckboxSelection: true,

      },
      {
        headerName: "Delivery date",
        field: "deliveryDate",
        type: "dateColumn",
        valueFormatter: dateFormatter,
        flex: 2,
      },
      {
        headerName: "Order amount",
        field: "total",
        type: "number",
        flex: 2,
      },
      {
        headerName: "",
        field: "changeDeliveryDate",
        flex: 2,
        cellRenderer: ChangeDateCellRenderer,
      },
    ], []
  );

  return (
    <>
      <NoCards
        pageTitle={
          unprocessedViewDto?.orderType
            ? `Unconfirmed orders for ${ItemType[unprocessedViewDto?.orderType!]}`
            : undefined
        }
        className={"unprocessed-orders"}
      >
        {!isFetching && isLoaded ? (
          <>
            <Col lg={12}>
              {unprocessedViewDto?.fromDate && (
                <DeliveryDatePicker
                  deliveryDates={possibleDeilveryDates}
                  currentValue={
                    currentDeliveryDate?.actualValue ?? unprocessedViewDto?.fromDate!
                  }
                  setDeliveryDate={setDeliveryDate}
                  size="lg"
                />
              )}
            </Col>
            {unprocessedViewDto?.orders && unprocessedViewDto.orders?.length > 0 ? (
              <Col lg={12}>
                <Card className="shadow-sm" border="light">
                  <div className="ag-theme-alpine ag-row-pointer available-products-container">
                    <AgGridReact
                      onGridReady={onGridReady}
                      onSelectionChanged={onSelectionChanged}
                      columnDefs={columnDefs}
                      rowData={unprocessedViewDto.orders}
                      domLayout="autoHeight"
                      rowSelection="multiple"
                      pagination={true}
                      paginationPageSize={100}
                    />
                  </div>
                </Card>
              </Col>
            ) : (
              <Col lg={12}>
                <Card className="shadow-sm" border="light">
                  <div className="no-items">No orders available.</div>
                </Card>
              </Col>
            )}
            <Col lg={12} className={"d-flex flex-row-reverse"}>
              <Stack gap={3} direction="horizontal">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={ selectedCount === 0 }
                >
                  {"Delete"}
                </Button>
                <LinkContainer to={"/"}>
                  <Button type="button" variant="light">
                    {"Back"}
                  </Button>
                </LinkContainer>
              </Stack>
            </Col>
          </>
        ) : (
          <>
            <Col lg={12}>
              <h3> </h3>
            </Col>
            <Col lg={12}>
              <Card className="shadow-sm" border="light">
                <CardPlaceholder />
              </Card>
            </Col>
          </>
        )}
      </NoCards>
      <Modal show={error?.length! > 0} onHide={() => setError(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{error}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setError(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {showDeleteDialog && (
        <DeleteConfirmModal
          show={showDeleteDialog}
          numOfOrders={selectedCount}
          cancelHandler={() => setShowDeleteDialog(false)}
          confirmHandler={deleteOrders}
        />
      )}
      {itemUpateDate !== null && (
        <UpdateDateConfirmModal
          orderId={itemUpateDate.orderId}
          show={itemUpateDate !== null}
          updateOrderDate={updateOrderDate}
          handleClose={() => setItemUpateDate(null)}
          storeName={itemUpateDate?.store?.name!}
          date={itemUpateDate?.deliveryDate!}
          orderAmount={itemUpateDate?.total!}
          possibleDelieryDates={unprocessedViewDto?.newDeliveryDates!}
        />
      )}
    </>
  );
};
